import { FC, useCallback, useContext, useMemo, useRef, useState } from "react";
import { Prompt, useHistory } from "react-router-dom";
import { Controller, FormProvider, useForm } from "react-hook-form";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";
import { pl } from "date-fns/locale/pl";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import __ from "src/utils/Translations";
import {
  getAllWeekdaysInYear,
  getExcludedWeekDays,
  getYearRange,
  weekDayOptions,
} from "src/utils/Company/Absences/checkExcludedWeekDays";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import { absencesAbsencesConfigurationWrite } from "src/utils/RoleBasedSecurity/permissions";
import { GlobalModalContext } from "src/utils/GlobalModal/GlobalModalContext";
import { LogLevel, useLogStatus } from "src/utils/newRelic/useLogStatus";
import { useSaveCalendar } from "src/Pages/Company/Absences/api/useCalendars";
import Fieldset from "src/Pages/Company/Absences/Forms/Fieldset";
import { ControlledTextField } from "@benefit-systems/hr-tools-ui";
import CalendarPicker from "src/Pages/Company/Absences/Calendars/Calendar/CalendarPicker/CalendarPicker";
import CalendarLegend from "src/Pages/Company/Absences/Calendars/Calendar/CalendarLegend/CalendarLegend";
import CalendarExcludeDayForm from "src/Pages/Company/Absences/Calendars/Calendar/CalendarExcludeDayForm/CalendarExcludeDayForm";
import {
  CalendarAction,
  CalendarPickerVariant,
  ICalendar,
  IExcludedDay,
  IExcludedWeekDay,
} from "src/Pages/Company/Absences/Calendars/Calendars.models";
import { calendarFormSchema } from "src/Pages/Company/Absences/Calendars/Calendar/CalendarForm.schema";
import useNativeConfirmBeforeUnload from "src/utils/jsHelpers/useNativeConfirmBeforeUnload";

interface ICalendarFormProps {
  calendarId?: string;
  calendarData?: ICalendar;
  calendarNames?: string[];
}

export interface IFormValues {
  name: string;
  description: string;
  year: string;
  configurationOptions: {
    absencePlanningEnabled: boolean;
  };
}

const CalendarForm: FC<ICalendarFormProps> = ({
  calendarId,
  calendarData,
  calendarNames = [],
}) => {
  const history = useHistory();
  const companyId = useCompanyId();
  const canWriteAbsencesConfiguration = usePermission(
    absencesAbsencesConfigurationWrite
  );
  const logStatus = useLogStatus();
  const { setModal, resetModal } = useContext(GlobalModalContext);
  const isNew = !calendarId;
  const currentYear = new Date().getFullYear().toString();
  const availableYearOptions = getYearRange(
    calendarData?.year || currentYear,
    4,
    4
  );
  const previousYearRef = useRef(calendarData?.year || currentYear);

  if (!companyId) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No company ID provided.",
      file: "CalendarForm.tsx",
    });
  }

  if (!calendarId) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No calendar ID provided.",
      file: "CalendarForm.tsx",
    });
  }

  const [excludedWeekDays, setExcludedWeekDays] = useState<IExcludedWeekDay[]>(
    getExcludedWeekDays(
      calendarData?.excludedDays || [],
      calendarData?.year || currentYear
    )
  );

  const [excludedDays, setExcludedDays] = useState<IExcludedDay[]>(
    calendarData?.excludedDays || []
  );

  useNativeConfirmBeforeUnload();

  const memoizedExcludedWeekDays = useMemo(
    () => excludedWeekDays.map((item) => item.id),
    [excludedWeekDays]
  );

  const getDefaultName = useCallback(() => {
    if (!calendarData?.name) {
      return "";
    }

    const baseName = calendarData.name;

    if (!isNew) return baseName;

    // Helper function to find next available name with counter
    const findAvailableName = (baseTemplate: string, startCounter = 1) => {
      let counter = startCounter;
      let candidateName =
        startCounter === 0 ? baseTemplate : `${baseTemplate}${counter}`;

      while (calendarNames.includes(candidateName)) {
        counter += 1;
        candidateName = `${baseTemplate}${counter}`;
      }

      return candidateName;
    };

    // Check if the base name already ends with " duplikat"
    const endsWithDuplikat = baseName.endsWith(" duplikat");

    if (endsWithDuplikat) {
      // If it already ends with " duplikat", start with numbered duplicates
      return findAvailableName(baseName);
    }

    // For names that don't end with " duplikat", try the base + " duplikat" first
    const duplicateTemplate = `${baseName} duplikat`;
    if (!calendarNames.includes(duplicateTemplate)) {
      return duplicateTemplate;
    }

    // If base + " duplikat" is taken, find numbered version
    return findAvailableName(duplicateTemplate);
  }, [calendarData?.name, calendarNames, isNew]);

  const formMethods = useForm<IFormValues>({
    resolver: standardSchemaResolver(calendarFormSchema),
    mode: "onChange",
    defaultValues: {
      name: getDefaultName(),
      description: calendarData?.description || "",
      year: calendarData?.year || currentYear,
      configurationOptions: {
        absencePlanningEnabled:
          calendarData?.configurationOptions?.absencePlanningEnabled || true,
      },
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    setValue,
  } = formMethods;

  const chosenYear = watch("year");

  const handleWeekDayDisabling = useCallback(
    (weekDays: IExcludedWeekDay[]) => {
      const wholeYear = weekDays.reduce<IExcludedDay[]>((acc, weekDay) => {
        const allWeekDates = getAllWeekdaysInYear(
          Number(chosenYear),
          weekDay.id
        );
        return [...acc, ...allWeekDates];
      }, []);

      const holidays = new Map(
        excludedDays.filter((day) => day.label).map((day) => [day.date, day])
      );

      const mergedDays = [...wholeYear];

      // Add existing days with labels that are not already in wholeYear
      excludedDays.forEach((day) => {
        if (day.label && !wholeYear.some((d) => d.date === day.date)) {
          mergedDays.push(day);
        }
      });

      // Remove duplicates, preferring holidays
      const uniqueDays = Array.from(
        new Map(
          mergedDays.map((day) => [day.date, holidays.get(day.date) || day])
        ).values()
      );

      setExcludedWeekDays(weekDays);
      setExcludedDays(uniqueDays);
    },
    [chosenYear, excludedDays]
  );

  const {
    mutate: saveCalendar,
    isPending,
    isSuccess,
  } = useSaveCalendar({
    companyId,
    calendarId,
    isNew,
  });

  const onSubmit = (formData: IFormValues) => {
    if (isNew) {
      saveCalendar({ formData, excludedDays });
    } else {
      setModal({
        open: true,
        title: __("Czy na pewno chcesz dokonać edycji?"),
        description: __(
          "Jeżeli podczas edycji oznaczono lub usunięto dni wolne, nastąpi ponowne przeliczenie wniosków, które jeszcze się nie odbyły w oparciu o nowy kalendarz. Wnioski, które już się rozpoczęły lub odbyły nie zostaną ponownie przeliczone."
        ),
        actions: [
          {
            label: __("Anuluj"),
            variant: "outlined",
            callbackFn: resetModal,
          },
          {
            label: __("Zapisz"),
            callbackFn: () => {
              saveCalendar({ formData, excludedDays });
              resetModal();
            },
          },
        ],
      });
    }
  };

  const handleYearChange = useCallback(
    (event: SelectChangeEvent) => {
      // Reset excluded days on year change
      if (event.target.value !== previousYearRef.current) {
        setModal({
          open: true,
          title: __("Czy na pewno chcesz dokonać zmiany roku?"),
          description: __(
            "Zmiana roku spowoduje usunięcie wszystkich pozycji wcześniej dodanych do utworzonego kalendarza dni wolnych."
          ),
          actions: [
            {
              label: __("Anuluj"),
              variant: "outlined",
              callbackFn: resetModal,
            },
            {
              label: __("Dokonaj zmiany"),
              callbackFn: () => {
                previousYearRef.current = event.target.value;
                setValue("year", event.target.value);
                setExcludedDays([]);
                setExcludedWeekDays([]);
                resetModal();
              },
            },
          ],
        });
      }
    },
    [resetModal, setModal, setValue]
  );

  const handleCalendarDayAction = useCallback(
    (action: CalendarAction, day: Date) => {
      if (typeof setModal === "function") {
        if (action === CalendarAction.ADD) {
          setModal({
            open: true,
            title: __("Dzień wolny dla:"),
            customElements: (
              <CalendarExcludeDayForm
                day={day}
                setExcludedDays={setExcludedDays}
              />
            ),
            actions: null,
          });
        } else if (action === CalendarAction.DELETE) {
          setModal({
            open: true,
            title: __(
              "Czy na pewno chcesz usunąć wskazany dzień z kalendarza?"
            ),
            actions: [
              {
                label: __("Anuluj"),
                variant: "outlined",
                callbackFn: resetModal,
              },
              {
                label: __("Zapisz"),
                callbackFn: () => {
                  setExcludedDays([
                    ...excludedDays.filter(
                      (item) => format(day, "yyyy-MM-dd") !== item.date
                    ),
                  ]);
                  resetModal();
                },
              },
            ],
          });
        }
      }
    },
    [excludedDays, resetModal, setModal]
  );

  const handleGoBack = useCallback(() => {
    if (typeof setModal === "function") {
      setModal({
        open: true,
        title: __("Chcesz wyjść z tworzenia kalendarza rocznego?"),
        description: __("Zmiany nie zostaną zapisane."),
        actions: [
          {
            label: __("Kontynuuj tworzenie"),
            variant: "outlined",
            callbackFn: () => {
              resetModal();
            },
          },
          {
            label: __("Wyjdź"),
            color: "error",
            callbackFn: () => {
              resetModal();
              history.goBack();
            },
          },
        ],
      });
    }
  }, [history, resetModal, setModal]);

  const showLegend = useCallback(() => {
    if (typeof setModal === "function") {
      setModal({
        open: true,
        title: __("Legenda dodanych dni wolnych"),
        customElements: <CalendarLegend excludedDays={excludedDays} />,
        actions: [
          {
            label: __("Zamknij"),
            callbackFn: () => {
              resetModal();
            },
          },
        ],
      });
    }
  }, [excludedDays, resetModal, setModal]);

  const calendars = useMemo(
    () =>
      Array.from(new Array(12)).map((item, index) => (
        <CalendarPicker
          year={chosenYear}
          month={index}
          excludedDays={excludedDays}
          excludedWeekDays={memoizedExcludedWeekDays}
          variant={CalendarPickerVariant.PICKER}
          handleCalendarDayAction={handleCalendarDayAction}
        />
      )),
    [
      chosenYear,
      excludedDays,
      handleCalendarDayAction,
      memoizedExcludedWeekDays,
    ]
  );

  return (
    <>
      <FormProvider {...formMethods}>
        <Paper
          component="form"
          noValidate
          onSubmit={handleSubmit(onSubmit)}
          sx={{ p: "2rem" }}
          data-testid="absences-calendar-form"
        >
          <Fieldset
            legend={__("Informacje ogólne")}
            helpText={__(
              "Wpisz czytelną i prostą nazwę dla tworzonego kalendarza - będzie ona wykorzystywana m.in. w celu przypisania kalendarzy pracownikom plikiem importu."
            )}
          >
            <Controller
              name="configurationOptions.absencePlanningEnabled"
              control={control}
              render={({ field: { value, onChange } }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!value}
                      onChange={onChange}
                      data-testid="absences-calendar-form-field__absence-planning-enabled"
                    />
                  }
                  label={__("Możliwość planowania rocznego planu nieobecności")}
                />
              )}
            />
            <ControlledTextField
              name="name"
              label={__("Nazwa kalendarza")}
              required
              fullWidth
              maxLength={100}
              disabled={!canWriteAbsencesConfiguration}
              data-testid="absences-calendar-form-field__name"
              normalizeSpaces
            />
            <ControlledTextField
              name="description"
              label={__("Opis")}
              multiline
              maxRows={3}
              maxLength={255}
              disabled={!canWriteAbsencesConfiguration}
              data-testid="absences-calendar-form-field__description"
            />
          </Fieldset>
          <Fieldset
            legend={__("Rok")}
            helpText={
              isNew
                ? null
                : __("Zmiana roku nie jest możliwa dla edytowanych kalendarzy.")
            }
          >
            <FormControl fullWidth>
              <InputLabel id="year-label" required>
                {__("Rok")}
              </InputLabel>
              <Controller
                name="year"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    label={__("Rok")}
                    labelId="year-label"
                    required
                    onChange={handleYearChange}
                    disabled={!canWriteAbsencesConfiguration || !isNew}
                    data-testid="absences-calendar-form-field__year"
                  >
                    {availableYearOptions.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              <FormHelperText error={!!errors.year}>
                {errors.year?.message}
              </FormHelperText>
            </FormControl>
          </Fieldset>
          <Fieldset
            legend={__("Dodaj dni wolne od pracy w skali tygodnia:")}
            helpText={__(
              "Dodawanie dni wolnych: Wybierz dni tygodnia (np. soboty, niedziele), które mają być oznaczane jako wolne w kalendarzu."
            )}
          >
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={weekDayOptions}
              value={excludedWeekDays}
              disabled={!canWriteAbsencesConfiguration}
              onChange={(event, newValue) => {
                handleWeekDayDisabling(newValue);
              }}
              getOptionLabel={(option) => __(option.label)}
              renderOption={(props, option, { selected }) => {
                // eslint-disable-next-line react/prop-types
                const { key, ...optionProps } = props;
                return (
                  <li key={key} {...optionProps}>
                    <Checkbox
                      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {__(option.label)}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={__("Wybierz dni wolne")}
                  data-testid="absences-calendar-form-field__weekdays"
                />
              )}
            />
          </Fieldset>
          <Fieldset
            legend={__("Stwórz kalendarz dni wolnych:")}
            helpText={__(
              "Wybór roku: Upewnij się, że wybrałeś właściwy rok, ponieważ zmiana roku usunie wcześniej dodane dni wolne."
            )}
            divider={false}
          >
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={pl}
            >
              <Stack
                direction="row"
                flexWrap="nowrap"
                useFlexGap
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="body1">
                  {__(
                    "Dodawaj i usuwaj dni wolne klikając w poszczególne dni. Aby zobaczyć stworzony zakres przejdź do Legendy."
                  )}
                </Typography>
                <Button variant="text" type="button" onClick={showLegend}>
                  {__("Zobacz legendę")}
                </Button>
              </Stack>
              <Stack
                direction="row"
                spacing={4}
                flexWrap="wrap"
                useFlexGap
                mt="2rem"
              >
                {calendars}
              </Stack>
              {canWriteAbsencesConfiguration && (
                <Box
                  sx={{
                    textAlign: "right",
                    margin: "2rem 0 0 1rem",
                  }}
                >
                  <Button
                    variant="outlined"
                    type="button"
                    size="large"
                    sx={{ marginRight: "1rem" }}
                    onClick={handleGoBack}
                  >
                    {__("Anuluj")}
                  </Button>
                  <Button
                    variant="contained"
                    type="submit"
                    size="large"
                    loading={isPending}
                    loadingPosition="start"
                    data-testid="absences-calendar-form__button-submit"
                  >
                    {__("Zapisz kalendarz")}
                  </Button>
                </Box>
              )}
            </LocalizationProvider>
          </Fieldset>
        </Paper>
        <Prompt
          when={isDirty && !isSuccess}
          message={__("Niezapisane zmiany zostaną utracone.")}
        />
      </FormProvider>
    </>
  );
};

export default CalendarForm;
